import { defaultUseCaseTranslator } from '../hooks/useUseCaseTranslate'

const ACCEPTED_FILE_TYPES = 'image/jpeg, image/png, image/gif, image/webp, image/avif, video/quicktime, video/mp4'
const DISALLOWED_FILE_EXTENSIONS = ['.jfif', '.jif', '.jfi']
const MAX_FILE_SIZE = 5.5 * 1024 * 1024 * 1024 // 5.5GB

const SLICER_ACCEPTED_FILE_TYPES = '.3mf,.curaprofile,.fff,.gcode,.ini,.stlproj'
const SLICER_MAX_FILE_SIZE = 1 * 1024 * 1024 * 1024 // 1GB

export const bytesToGB = (bytes, decimals = 2) => {
  return (bytes / (1024 * 1024 * 1024)).toFixed(decimals)
}

export const formatBytes = (bytes) => {
  if (bytes === 0) return '0 B'

  const units = ['B', 'KB', 'MB', 'GB']
  const k = 1024
  const i = Math.floor(Math.log(bytes) / Math.log(k))

  return `${parseFloat((bytes / k ** i).toFixed(2))} ${units[i]}`
}

export const getFileExtension = (fileName = '') => {
  const lastDotIndex = fileName.lastIndexOf('.')
  if (lastDotIndex === -1) return ''
  return fileName.slice(lastDotIndex).toLowerCase()
}

export const getFileNameWithoutExtension = (fileName = '') => {
  const lastDotIndex = fileName.lastIndexOf('.')
  if (lastDotIndex === -1) return fileName
  return fileName.slice(0, lastDotIndex)
}

export const validateSlicerFile = (file, t = defaultUseCaseTranslator.t) => {
  if (file.size > SLICER_MAX_FILE_SIZE) {
    return {
      valid: false,
      error: t('errors.slicerTooLarge'),
      errorType: 'size',
    }
  }

  const acceptedExtensions = SLICER_ACCEPTED_FILE_TYPES.split(',')

  if (!acceptedExtensions.includes(getFileExtension(file.name))) {
    return {
      valid: false,
      error: t('errors.slicerUnsupported'),
      errorType: 'type',
    }
  }

  return { valid: true, error: null, errorType: null }
}

export const splitFilesByExtension = (files) => files.reduce((result, file) => {
  if (DISALLOWED_FILE_EXTENSIONS.includes(getFileExtension(file.name))) {
    result.disallowed.push(file)
  } else {
    result.allowed.push(file)
  }
  return result
}, { allowed: [], disallowed: [] })

export const validateFiles = (files, t = defaultUseCaseTranslator.t) => {
  const fileArray = Array.from(files)

  // First split by extension
  const { allowed, disallowed } = splitFilesByExtension(fileArray)

  const errors = []

  // Add error for disallowed extensions
  if (disallowed.length > 0) {
    errors.push(t('errors.unsupportedImage'))
  }

  if (allowed.length === 0) {
    return { validFiles: [], errors }
  }

  const validFiles = []
  const acceptedTypes = ACCEPTED_FILE_TYPES.split(', ').map(type => type.trim())

  allowed.forEach((file) => {
    if (file.size > MAX_FILE_SIZE) {
      errors.push(t('errors.fileTooLarge', { name: file.name }))
      return
    }

    const fileType = file.type

    if (!acceptedTypes.includes(fileType)) {
      errors.push(t('errors.unsupportedFileType', { name: file.name }))
      return
    }

    validFiles.push(file)
  })

  return { validFiles, errors }
}

export const getValidFiles = (files, currentFileCount, maxFiles, t = defaultUseCaseTranslator.t) => {
  const { validFiles, errors } = validateFiles(files, t)

  if (validFiles.length === 0) {
    return { validFiles: [], errors }
  }

  const totalFiles = currentFileCount + validFiles.length

  if (totalFiles > maxFiles) {
    return {
      validFiles: [],
      errors: [...errors, t('errors.maxFiles', { count: maxFiles })],
    }
  }

  return { validFiles, errors }
}

export {
  ACCEPTED_FILE_TYPES,
  DISALLOWED_FILE_EXTENSIONS,
  MAX_FILE_SIZE,
  SLICER_ACCEPTED_FILE_TYPES,
  SLICER_MAX_FILE_SIZE,
}
